import {
  Button,
  Icon,
  SearchBar,
  Toast,
  type Column,
  type ToastType,
} from "@legal-500/legal500-design-system";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ListEnvelope, ListParams } from "../../api/registrationGateLists.service";
import { DataTable } from "../../components/DataTable/DataTable";
import { PaginationControls } from "../../components/PaginationControls/PaginationControls";
import LoadingState from "../../components/LoadingState";
import EmptyState from "../../components/EmptyState";
import PermissionGate from "../../components/Permissions/PermissionGate";
import { UnauthorizedView } from "../../components/UnauthorizedView";
import {
  RegistrationGateAddModal,
  type RegistrationGateListKind,
} from "./RegistrationGateAddModal";
import { RegistrationGateRemoveModal } from "./RegistrationGateRemoveModal";
import styles from "./RegistrationGate.module.css";

const ITEMS_PER_PAGE = 25;

/** Pull an HTTP status off whatever the client threw (client.ts sets `.status`). */
function getErrorStatus(err: unknown): number | undefined {
  if (err && typeof err === "object") {
    const e = err as { status?: number; statusCode?: number };
    return e.status ?? e.statusCode;
  }
  return undefined;
}

/**
 * Everything the section needs to offer an "Add" flow for its list. Passed from
 * `RegistrationGate.tsx` alongside `list`/`columns` so the section stays generic:
 * `kind` selects the modal copy/validation, `submit` wraps the Phase 1 add
 * service into a uniform call, and `getValue` reads the stored value off the
 * created row for the success toast (the allow-list value may be normalised).
 */
export type RegistrationGateAddSupport<T> = {
  kind: RegistrationGateListKind;
  submit: (input: { value: string; reason?: string }) => Promise<T>;
  getValue: (row: T) => string;
};

/**
 * Everything the section needs to offer a per-row "Remove" flow. Passed from
 * `RegistrationGate.tsx` alongside `list`/`columns` so the section stays generic:
 * `kind` selects the confirm-modal copy, `submit` wraps the Phase 1 remove
 * service (the optional reason is encoded as a `?reason=` query param by the
 * service — DELETE has no body), and `getValue` reads the stored value off the
 * row for the confirm copy + success toast.
 */
export type RegistrationGateRemoveSupport<T> = {
  kind: RegistrationGateListKind;
  submit: (id: string, reason?: string) => Promise<void>;
  getValue: (row: T) => string;
};

type RegistrationGateListSectionProps<T extends { id: string }> = {
  /** Section heading, e.g. "Blocked domains". */
  title: string;
  /** Short line under the heading saying what the list governs. */
  description?: string;
  /** Noun used in pagination + empty-state copy, e.g. "domains". */
  itemName: string;
  searchPlaceholder: string;
  /** Phase 1 service list method returning the `{ data, meta }` envelope. */
  list: (params: ListParams) => Promise<ListEnvelope<T>>;
  columns: Column<T>[];
  /** Add flow config; when provided a (write-gated) "Add" button is rendered. */
  add?: RegistrationGateAddSupport<T>;
  /**
   * Remove flow config; when provided a (write-gated) per-row "Remove" action
   * column is appended and confirm-to-remove is wired up.
   */
  remove?: RegistrationGateRemoveSupport<T>;
};

/**
 * One self-contained read-only list block (heading + search + table + pagination)
 * used for both the blocked-domains and the profanity allow-list sections.
 *
 * Each instance owns its own search + pagination state so the two sections on the
 * page never collide. Pagination is driven by `meta.total` from the service.
 */
export function RegistrationGateListSection<T extends { id: string }>({
  title,
  description,
  itemName,
  searchPlaceholder,
  list,
  columns,
  add,
  remove,
}: RegistrationGateListSectionProps<T>) {
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [unauthorized, setUnauthorized] = useState(false);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  // Bumping this re-runs the (cancellation-safe) load effect — used as reload().
  const [reloadToken, setReloadToken] = useState(0);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  // The row whose per-row Remove is being confirmed (null = confirm modal closed).
  const [rowToRemove, setRowToRemove] = useState<T | null>(null);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Refetch this section's current page (called after a successful add). */
  function reload() {
    setReloadToken(token => token + 1);
  }

  function handleSearchChange(value: string) {
    setSearchTerm(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setCurrentPage(1);
      setDebouncedSearch(value);
    }, 300);
  }

  function handlePageChange(page: number) {
    setCurrentPage(page);
  }

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError(null);
        setUnauthorized(false);
        const offset = (currentPage - 1) * ITEMS_PER_PAGE;
        const response = await list({
          search: debouncedSearch || undefined,
          limit: ITEMS_PER_PAGE,
          offset,
        });
        if (cancelled) return;
        setRows(response.data);
        setTotal(response.meta.total);
      } catch (err) {
        if (cancelled) return;
        const status = getErrorStatus(err);
        // A 401/403 on the list call means the token lost read access — surface the
        // shared UnauthorizedView rather than a generic "failed to load" error.
        if (status === 401 || status === 403) {
          setUnauthorized(true);
        } else {
          setError(err instanceof Error ? err : new Error(`Failed to load ${itemName}`));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [currentPage, debouncedSearch, list, itemName, reloadToken]);

  function handleAddSuccess(createdRow: T) {
    if (!add) return;
    // The allow-list canonicalises on write, so show the stored value from the
    // created row (the service response), not what the user typed.
    setToast({ message: `Added "${add.getValue(createdRow)}"`, type: "success" });
    setIsAddModalOpen(false);
    reload();
  }

  function handleRemoveSuccess(removedValue: string) {
    setToast({ message: `Removed "${removedValue}"`, type: "success" });
    setRowToRemove(null);
    reload();
  }

  /**
   * When a `remove` config is provided, append a write-gated per-row "Remove"
   * action column so both sections get it uniformly. A read-only actor (without
   * `staff.registration_gate.write`) sees an empty cell — the PermissionGate hides the
   * control entirely.
   */
  const tableColumns = useMemo<Column<T>[]>(() => {
    if (!remove) return columns;
    const actionsColumn: Column<T> = {
      header: "Actions",
      accessor: "id",
      render: (_value, row) => (
        <PermissionGate permission="staff.registration_gate.write">
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={event => {
              event.stopPropagation();
              setRowToRemove(row);
            }}
          >
            Remove
          </Button>
        </PermissionGate>
      ),
    };
    return [...columns, actionsColumn];
  }, [columns, remove]);

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <div>
          <h2 className={styles.sectionTitle}>{title}</h2>
          {description && <p className={styles.sectionDescription}>{description}</p>}
        </div>
        {add && (
          <PermissionGate permission="staff.registration_gate.write">
            <Button variant="contained" color="primary" onClick={() => setIsAddModalOpen(true)}>
              <Icon icon="plus" size="small" />
              Add
            </Button>
          </PermissionGate>
        )}
      </div>

      <div className={styles.searchSection}>
        <SearchBar
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder={searchPlaceholder}
        />
      </div>

      <div className={styles.contentSection}>
        {unauthorized ? (
          <UnauthorizedView />
        ) : error ? (
          <EmptyState
            variant="error"
            title="Failed to load"
            message={`Failed to load ${itemName}: ${error.message}`}
          />
        ) : loading ? (
          <LoadingState />
        ) : (
          <>
            <DataTable
              data={rows}
              columns={tableColumns}
              emptyMessage={`No ${itemName} found.`}
              rowKey="id"
            />
            <PaginationControls
              currentPage={currentPage}
              totalPages={Math.ceil(total / ITEMS_PER_PAGE)}
              startIndex={(currentPage - 1) * ITEMS_PER_PAGE}
              endIndex={currentPage * ITEMS_PER_PAGE}
              totalCount={total}
              itemName={itemName}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>

      {add && (
        <RegistrationGateAddModal<T>
          isOpen={isAddModalOpen}
          kind={add.kind}
          add={add.submit}
          onSuccess={handleAddSuccess}
          onClose={() => setIsAddModalOpen(false)}
        />
      )}

      {remove && rowToRemove && (
        <RegistrationGateRemoveModal
          isOpen
          kind={remove.kind}
          value={remove.getValue(rowToRemove)}
          remove={reason => remove.submit(rowToRemove.id, reason)}
          onSuccess={() => handleRemoveSuccess(remove.getValue(rowToRemove))}
          onClose={() => setRowToRemove(null)}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={toast.type === "error" ? 5000 : 3000}
        />
      )}
    </section>
  );
}
